// Roles y Permisos · pestaña «Agregar permisos» (Reybier, 23-oct): lógica pura, sin Angular.
//
// La pestaña abre una pantalla real en modo marcador, el usuario toca lo que quiere proteger y esto arma lo que se
// manda a POST /api/admin/rbac/agregar-permisos (contrato en el API: controllers/rbac-admin.js). Aquí vive todo lo que
// no necesita Angular para probarse: aplanar las rutas del router, proponer nombre y módulo, armar el cuerpo del POST,
// contar permisos, validar y traducir el 409. Se prueba con node:test (test/agregar-permisos-logica.test.mjs).
//
// OJO con los tipos: CI no instala `typescript`, así que la prueba se los quita con expresiones regulares. Por eso
// aquí solo se anota `x: any | string | number | boolean` en parámetros y variables, y los `interface` van al borde
// izquierdo (cierran con `}` en la columna 0). Nada de `as`, genéricos ni tipos de retorno.

// ── Tipos que usa el componente (la prueba los descarta) ──
export interface RutaConsola {
  ruta: string;
  parametros: string[];
  nombre: string;
}
export interface Huella {
  selector: string;
  texto: string;
  etiqueta: string;
  /** «Solo este»: posición entre los elementos que comparten selector y texto (sin ella, todos). */
  indice?: number;
}
export interface ServidorRuta {
  metodo: string;
  ruta: string;
}
export interface AccionMarcada {
  id: string;
  casilla: string;
  permisoExistente: string;
  permisoTexto: string;
  huella: Huella;
  servidor: ServidorRuta | null;
}
export interface SeccionMarcada {
  id: string;
  nombre: string;
  huella: Huella;
}
export interface EstadoAgregar {
  rutaPatron: string;
  pantalla: string;
  grupo: string;
  nombre: string;
  despuesDe: string;
  filaExistente: any;
  acciones: AccionMarcada[];
  secciones: SeccionMarcada[];
  contador: number;
}

// ── Nombres y códigos (igual que el API) ──

/** minúsculas, sin tildes, todo lo que no sea [a-z0-9] pasa a `_`, sin `_` repetidos ni en los bordes, máx. 40. */
export const slug = (texto: any) =>
  String(texto == null ? '' : texto)
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40)
    .replace(/_+$/, '');

/** Casilla → acción del código (`config.<fila>.create`). Cualquier otra casilla usa su slug. */
export const ACCION_CODIGO: any = {
  Crear: 'create', Editar: 'update', Eliminar: 'delete', Consultar: 'read',
  Exportar: 'export', Aprobar: 'approve', Anular: 'cancel', Descargar: 'download'
};

/** Casillas con nombre estándar (Consultar ya viene sola en cada submódulo, no se ofrece). */
export const ACCIONES_ESTANDAR: string[] = Object.keys(ACCION_CODIGO).filter((c) => c !== 'Consultar');

/** La acción que lleva el código de una casilla: `Crear` → `create`; `Cerrar día` → `cerrar_dia`. */
export const codigoAccion = (casilla: any) =>
  Object.prototype.hasOwnProperty.call(ACCION_CODIGO, casilla) ? ACCION_CODIGO[casilla] : slug(casilla);

/** Método del servidor que normalmente va con esa casilla (para ofrecer primero esas rutas). */
export const metodoSugerido = (casilla: any) => {
  const m: any = { Crear: 'POST', Editar: 'PUT', Eliminar: 'DELETE', Consultar: 'GET', Exportar: 'GET', Descargar: 'GET' };
  return Object.prototype.hasOwnProperty.call(m, casilla) ? m[casilla] : '';
};

const recortar = (t: any) => String(t == null ? '' : t).replace(/\s+/g, ' ').trim();

/** «crear» → «Crear»: la primera letra en mayúscula para que coincida con los nombres estándar. */
export const capitalizar = (t: any) => {
  const s = recortar(t);
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
};

const sinTildes = (t: any) => String(t == null ? '' : t).normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// ── Rutas de la consola (desde router.config) ──

// Roles y Permisos ya NO se excluye (2026-10-28, lo pidió Reybier): también se autogestiona desde aquí. La red de
// seguridad es el Super Administrador, cuyo comodín *.*.* cubre toda casilla creada desde la interfaz, y que el servidor
// nunca deja proteger las rutas /api/admin/rbac. La lista queda para excluir otra pantalla si hiciera falta.
const EXCLUIDAS: string[] = [];

const unir = (a: any, b: any) => [a, b].filter((x) => x !== '' && x != null).join('/');

/** `/console/person/profile/:code` → { ruta, parametros: ['code'], nombre: 'Profile' } */
export const rutaDe = (ruta: any) => ({
  ruta,
  parametros: String(ruta).split('/').filter((s) => s.charAt(0) === ':').map((s) => s.slice(1)),
  nombre: nombreDeRuta(ruta)
});

/** Nombre propuesto desde el último tramo sin parámetro: `tasas-de-mora` → «Tasas de mora». */
export const nombreDeRuta = (ruta: any) => {
  const tramos = String(ruta == null ? '' : ruta).split('/').filter((s) => s && s !== 'console' && s.charAt(0) !== ':');
  const ultimo = tramos.length ? tramos[tramos.length - 1] : '';
  const limpio = ultimo.replace(/[-_]+/g, ' ').trim();
  return limpio ? limpio.charAt(0).toUpperCase() + limpio.slice(1) : 'Pantalla nueva';
};

/**
 * Aplana `router.config` en las rutas de la consola que se pueden abrir: `console/...` sin redirecciones, sin
 * comodín y sin la propia pantalla de Roles y Permisos. Un padre con hijas solo cuenta si una hija vacía lo
 * abre de verdad; los módulos perezosos suman sus rutas solo cuando Angular ya los cargó (`_loadedConfig`).
 */
export const aplanarRutas = (config: any, prefijo: any = '') => {
  const salida: any[] = [];
  for (const r of config || []) {
    if (!r || typeof r.path !== 'string' || r.path === '**' || r.redirectTo !== undefined) continue;
    const completa = unir(prefijo, r.path);
    const hijas = r.children || (r._loadedConfig && r._loadedConfig.routes) || [];
    const sePuedeAbrir = !!(r.component || r.loadComponent || r.loadChildren);
    const hijaVacia = hijas.some((h: any) => h && h.path === '' && h.redirectTo === undefined);
    if (sePuedeAbrir && (!hijas.length || hijaVacia)) salida.push('/' + completa);
    if (hijas.length) for (const x of aplanarRutas(hijas, completa)) salida.push(x.ruta);
  }
  const vistas = new Set();
  const lista: any[] = [];
  for (const ruta of salida) {
    if (vistas.has(ruta)) continue;
    vistas.add(ruta);
    if (ruta.indexOf('/console/') !== 0) continue;
    if (EXCLUIDAS.some((e) => ruta === e || ruta.indexOf(e + '/') === 0)) continue;
    lista.push(rutaDe(ruta));
  }
  return lista.sort((a, b) => (a.ruta < b.ruta ? -1 : a.ruta > b.ruta ? 1 : 0));
};

/** Los permisos RBAC que declara una ruta en `data.permissions` (solo textos, sin repetir); `null` si no declara la llave. */
const permisosDeData = (data: any) => {
  const p = data && data.permissions;
  if (!Array.isArray(p)) return null;
  return p.filter((x: any, i: any) => typeof x === 'string' && x.trim() !== '' && p.indexOf(x) === i);
};

// Recorre `router.config` (igual que `aplanarRutas`) y devuelve los permisos de la entrada más profunda cuya ruta
// completa es `rutaPatron`; `null` si ninguna coincide.
const buscarPermisos = (config: any, rutaPatron: any, prefijo: any, heredados: any, padreConComponente: any) => {
  let encontrados: any = null;
  for (const r of config || []) {
    if (!r || typeof r.path !== 'string' || r.path === '**' || r.redirectTo !== undefined) continue;
    const completa = unir(prefijo, r.path);
    const propios = permisosDeData(r.data);
    const hereda = r.path === '' || !padreConComponente;
    const efectivos = propios !== null ? propios : (hereda ? heredados : []);
    if ('/' + completa === rutaPatron) encontrados = efectivos;
    const hijas = r.children || (r._loadedConfig && r._loadedConfig.routes) || [];
    if (hijas.length) {
      const deHijas = buscarPermisos(hijas, rutaPatron, completa, efectivos, !!(r.component || r.loadComponent));
      if (deHijas !== null) encontrados = deHijas;
    }
  }
  return encontrados;
};

/**
 * Los permisos que HOY exige una pantalla: el `data.permissions` de su ruta en `router.config`, recorrido igual que
 * `aplanarRutas` (mismo prefijo, redirecciones y comodín fuera, módulos perezosos ya cargados). Una ruta sin
 * `permissions` propio hereda el del padre como lo hace Angular (ruta vacía o padre sin componente). Si dos entradas
 * tienen la misma ruta (padre e hija vacía) vale la más profunda, que es la que se muestra. Sin permisos RBAC → [].
 */
export const permisosDeRuta = (config: any, rutaPatron: any) => buscarPermisos(config, rutaPatron, '', [], false) || [];

/** Sugerencias al escribir: desde 2 letras, por ruta o por nombre, primero las que coinciden con el nombre. */
export const buscarRutas = (rutas: any, texto: any, max: any = 8) => {
  const q = sinTildes(recortar(texto));
  if (q.length < 2) return [];
  const peso = (r: any) => (sinTildes(r.nombre).indexOf(q) >= 0 ? 0 : 1);
  return (rutas || [])
    .filter((r: any) => sinTildes(r.ruta).indexOf(q) >= 0 || sinTildes(r.nombre).indexOf(q) >= 0)
    .map((r: any, i: any) => ({ r, i, p: peso(r) }))
    .sort((a: any, b: any) => a.p - b.p || a.i - b.i)
    .slice(0, max)
    .map((x: any) => x.r);
};

/** Parámetros de la ruta sin valor de ejemplo todavía. */
export const parametrosFaltantes = (ruta: any, valores: any) =>
  String(ruta).split('/').filter((s) => s.charAt(0) === ':').map((s) => s.slice(1))
    .filter((p) => !recortar(valores && valores[p]));

/** Ruta lista para abrir: cada `:param` por su valor de ejemplo (codificado). */
export const llenarRuta = (ruta: any, valores: any) =>
  String(ruta).split('/').map((s) => (s.charAt(0) === ':' ? encodeURIComponent(recortar(valores && valores[s.slice(1)])) : s)).join('/');

// ── Módulo propuesto desde la ruta ──
// Misma idea que URL_MODULE_MAP de isProfile.guard.ts: el primer tramo de la ruta dice a qué pantalla de la matriz
// pertenece. Los ids son los de `rbac_pantallas` (config, contactos, cobranza...).
const MODULO_POR_RUTA: any[] = [
  ['parametros-sistema', 'config'], ['rates', 'config'], ['plans', 'config'], ['fees', 'config'],
  ['payment-options', 'config'], ['accounts-admin', 'config'], ['type_product', 'config'], ['codes', 'config'],
  ['codigos', 'config'], ['notifications', 'config'], ['async-admin', 'config'], ['app-config', 'config'],
  ['procesos-masivos', 'config'], ['recargas', 'config'], ['online', 'config'], ['users', 'config'],
  ['seguridad-avanzada', 'config'], ['shops', 'config'], ['domiciliation-config', 'config'], ['bdv-async', 'config'],
  ['multicomercio', 'multicomercio'], ['merchants', 'multicomercio'], ['liquidacion', 'multicomercio'],
  ['constacts', 'contactos'], ['create-constacts', 'contactos'], ['search-client', 'contactos'],
  ['person', 'contactos'], ['cliente', 'contactos'], ['psicometrico', 'contactos'], ['consult-quota', 'contactos'],
  ['invoice-detail', 'contactos'], ['payment', 'contactos'], ['post', 'contactos'],
  ['ventas', 'ventas'], ['invoice-dashboard', 'ventas'],
  ['finanzas2', 'finanzas'], ['finacial-dashboard', 'finanzas'], ['financial-payments', 'finanzas'],
  ['payments', 'finanzas'], ['cash-register', 'finanzas'], ['ganancias', 'finanzas'], ['payall-balance', 'finanzas'],
  ['cobranza', 'cobranza'], ['cartera', 'cobranza'], ['caja', 'cobranza'], ['installments-console', 'cobranza'],
  ['domiciliation', 'cobranza'], ['direct-debit', 'cobranza'], ['dashboard-direct-debit', 'cobranza'],
  ['notificacion-xlsx', 'cobranza'], ['banco-plaza', 'cobranza'], ['bdv-consultas', 'cobranza'],
  ['credito', 'credito'], ['credit-', 'credito'], ['inactivate-quota', 'credito'], ['approval-policies', 'credito'],
  ['code-dynamic', 'credito'], ['quota-history', 'credito'], ['carga-masiva', 'credito'],
  ['client-stadistic', 'credito'], ['solicitudes-credito', 'credito'], ['constacts-status', 'credito'],
  ['estadisticas', 'reportes'], ['stadistic', 'reportes'], ['export', 'reportes'],
  ['catalog-products', 'productos_compra'],
  ['insurance', 'seguros'], ['seguros', 'seguros']
];

/** La pantalla de la matriz que mejor le calza a la ruta; si no hay pista, la primera de la lista. */
export const moduloDeRuta = (ruta: any, pantallas: any) => {
  const ids = (pantallas || []).map((p: any) => p.id);
  const sinConsola = String(ruta).replace(/^\/console\//, '');
  let mejor = '';
  let largo = -1;
  for (const par of MODULO_POR_RUTA) {
    if (sinConsola.indexOf(par[0]) === 0 && par[0].length > largo && ids.indexOf(par[1]) >= 0) {
      mejor = par[1];
      largo = par[0].length;
    }
  }
  return mejor || (ids.length ? ids[0] : '');
};

// ── Estado de lo marcado ──

/** Estado inicial al abrir una ruta: la pantalla entera es el submódulo (siempre lleva Consultar). */
export const crearEstado = (rutaPatron: any, nombre: any, pantalla: any) => ({
  rutaPatron,
  pantalla,
  grupo: '',
  nombre,
  despuesDe: '',
  filaExistente: null,
  acciones: [],
  secciones: [],
  contador: 0
});

/** Al elegir el lugar, el grupo sigue al de esa fila; «Al final del módulo» deja el grupo que se haya elegido. */
export const grupoDeFila = (filas: any, nombreFila: any) => {
  const f = (filas || []).find((x: any) => x && x.nombre === nombreFila);
  return f && f.grupo ? f.grupo : '';
};

/**
 * La misma huella = el mismo elemento de la pantalla: mismo selector Y mismo texto (sin espacios de más). Solo el
 * selector no alcanza: dos botones hermanos con las mismas clases y distinto texto («Aprobar» y «Rechazar») comparten
 * selector, y marcar uno no debe abrir ni cambiar la marca del otro.
 */
export const mismaHuella = (a: any, b: any) =>
  !!(a && b && a.selector && a.selector === b.selector && recortar(a.texto) === recortar(b.texto)
    && (typeof a.indice === 'number' ? a.indice : -1) === (typeof b.indice === 'number' ? b.indice : -1));

/** Marca ya puesta sobre ese elemento (en este estado), si hay. */
export const buscarMarca = (estado: any, huella: any) => {
  for (const a of estado.acciones || []) if (mismaHuella(a.huella, huella)) return { tipo: 'accion', id: a.id, marca: a };
  for (const s of estado.secciones || []) if (mismaHuella(s.huella, huella)) return { tipo: 'seccion', id: s.id, marca: s };
  return null;
};

/** Permisos que el elemento tocado YA tiene (`data-rbac-permiso` del código o una marca guardada): [{ codigo, texto }]. */
export const yaTieneDeMensaje = (msg: any) => {
  const lista = msg && Array.isArray(msg.yaTiene) ? msg.yaTiene : [];
  const salida: any[] = [];
  for (const y of lista) {
    if (y && typeof y.codigo === 'string' && y.codigo && !salida.some((x) => x.codigo === y.codigo)) {
      salida.push({ codigo: y.codigo, texto: String(y.texto || y.codigo) });
    }
  }
  return salida;
};

/** «Ya tiene permiso: Módulo › Fila › Casilla» (varios, separados por « · »), o ''. */
export const textoYaTiene = (form: any) => {
  const l = form && Array.isArray(form.yaTiene) ? form.yaTiene : [];
  return l.length ? 'Ya tiene permiso: ' + l.map((y: any) => y.texto).join(' · ') : '';
};

/** «Solo este» / «todos» cuando varios elementos comparten huella: devuelve el formulario con la huella elegida. */
export const conAlcance = (form: any, alcance: any) => {
  if (!form || !form.huellaSolo || !form.huellaTodos) return form;
  const solo = alcance === 'solo';
  return { ...form, alcance: solo ? 'solo' : 'todos', huella: solo ? form.huellaSolo : form.huellaTodos, coincidencias: solo ? 1 : form.coincidencias };
};

/** Formulario que se abre cuando la pantalla avisa un toque (`rbac-marcador:toque`). */
export const formularioDeToque = (msg: any, estado: any) => {
  const huella = (msg && msg.huella) || { selector: '', texto: '', etiqueta: '' };
  const coincidencias = Number(msg && msg.coincidencias) || 0;
  const previa = buscarMarca(estado, huella);
  if (previa) {
    const esAccion = previa.tipo === 'accion';
    const code = esAccion ? previa.marca.permisoExistente || '' : '';
    return {
      id: previa.id,
      tipo: previa.tipo,
      nombre: esAccion ? previa.marca.casilla : previa.marca.nombre,
      huella,
      coincidencias,
      servidor: esAccion ? previa.marca.servidor : null,
      existente: true,
      modoPermiso: code ? 'existente' : 'nuevo',
      permisoExistente: code,
      permisoTexto: code ? previa.marca.permisoTexto || code : '',
      casillaSugerida: null,
      sinSugerencia: false,
      yaTiene: [],
      alcance: typeof huella.indice === 'number' ? 'solo' : 'todos',
      huellaSolo: null,
      huellaTodos: null
    };
  }
  const sug = (msg && msg.sugerencia) || {};
  const tipo = deducirTipo(sug);
  const propuesto = recortar(sug.nombre) || recortar(huella.texto);
  const nombre = tipo === 'seccion'
    ? (propuesto ? recortar(estado.nombre) + ' · ' + propuesto : recortar(estado.nombre) + ' · Sección')
    : capitalizar(propuesto);
  const form: any = {
    id: '', tipo, nombre, huella, coincidencias, servidor: null, existente: false,
    modoPermiso: 'nuevo', permisoExistente: '', permisoTexto: '', casillaSugerida: null,
    yaTiene: yaTieneDeMensaje(msg), alcance: 'todos',
    // Varios elementos con la misma huella: «solo este» (huella con posición) o «todos» (la huella sola).
    huellaSolo: coincidencias > 1 && msg && msg.huellaSolo && typeof msg.huellaSolo.indice === 'number' ? msg.huellaSolo : null,
    huellaTodos: coincidencias > 1 ? huella : null
  };
  // Sin nombre sugerido (ni texto, ni tooltip, ni icono conocido) el campo se destaca: lo escribe la persona.
  form.sinSugerencia = !recortar(nombre);
  // Si el botón se llama como una opción de la pantalla («Eliminar») se avisa, pero se sigue creando una casilla nueva.
  const casilla = tipo === 'accion' ? casillaDeFila(estado.filaExistente, nombre) : null;
  if (casilla) form.casillaSugerida = { code: casilla.code, nombre: casilla.nombre };
  return form;
};

/** Por qué no se puede marcar así (texto claro), o '' si está bien. */
export const problemaDeMarca = (estado: any, form: any) => {
  const nombre = recortar(form && form.nombre);
  if (form && form.tipo === 'accion' && form.modoPermiso === 'existente' && !form.permisoExistente) {
    return 'Elige cuál de las opciones que ya existen quieres usar.';
  }
  if (!nombre) return 'Ponle un nombre a este botón.';
  if (!slug(nombre)) return 'El nombre necesita al menos una letra o un número.';
  if (form.tipo === 'accion' && form.modoPermiso === 'existente') {
    if (!form.permisoExistente) return 'Elige cuál de las opciones que ya existen quieres usar.';
    const otra = (estado.acciones || []).find((a: any) => a.id !== form.id && a.permisoExistente === form.permisoExistente);
    if (otra) return 'Ya marcaste una acción con esa misma opción: elige otra.';
    return '';
  }
  if (form.tipo === 'accion') {
    const cod = codigoAccion(capitalizar(nombre));
    const yaEnFila = casillaDeFila(estado.filaExistente, capitalizar(nombre));
    if (yaEnFila) return 'Esta pantalla ya tiene la casilla «' + yaEnFila.nombre + '»: usa esa con «Usar una casilla que ya existe» o cambia el nombre.';
    if (cod === 'read') return 'Consultar ya viene incluida en cada pantalla de la matriz: elige otro nombre.';
    const otra = (estado.acciones || []).find((a: any) => a.id !== form.id && !a.permisoExistente && codigoAccion(a.casilla) === cod);
    if (otra) return 'Ya marcaste una acción «' + otra.casilla + '» en esta pantalla: elige otro nombre.';
  } else {
    const s = slug(nombre);
    if (s === slug(estado.nombre)) return 'Una sección no puede llamarse igual que la pantalla.';
    const otra = (estado.secciones || []).find((x: any) => x.id !== form.id && slug(x.nombre) === s);
    if (otra) return 'Ya marcaste una sección «' + otra.nombre + '»: elige otro nombre.';
  }
  return '';
};

const sinMarca = (lista: any, id: any) => (lista || []).filter((x: any) => x.id !== id);

/** Estado nuevo con la marca del formulario puesta (alta, o cambio si el elemento ya estaba marcado). */
export const aplicarMarca = (estado: any, form: any) => {
  const id = form.id || 'm' + (estado.contador + 1);
  const huella = form.huella;
  let acciones = sinMarca(estado.acciones, form.id);
  let secciones = sinMarca(estado.secciones, form.id);
  if (form.tipo === 'accion') {
    const servidor = form.servidor && form.servidor.metodo && form.servidor.ruta
      ? { metodo: form.servidor.metodo, ruta: form.servidor.ruta } : null;
    const existe = form.modoPermiso === 'existente' && form.permisoExistente;
    acciones = acciones.concat([{
      id, casilla: capitalizar(form.nombre), permisoExistente: existe ? form.permisoExistente : '',
      permisoTexto: existe ? form.permisoTexto || form.permisoExistente : '', huella, servidor
    }]);
  } else {
    secciones = secciones.concat([{ id, nombre: recortar(form.nombre), huella }]);
  }
  return { ...estado, acciones, secciones, contador: form.id ? estado.contador : estado.contador + 1 };
};

/** Estado nuevo sin esa marca (acción o sección). */
export const quitarMarca = (estado: any, id: any) =>
  ({ ...estado, acciones: sinMarca(estado.acciones, id), secciones: sinMarca(estado.secciones, id) });

/**
 * Cuántos permisos NUEVOS se crean: cada submódulo nuevo trae su Consultar, más las acciones con permiso nuevo.
 * Una pantalla que ya es una fila no crea Consultar, y una acción con «permiso que ya existe» no crea nada.
 */
export const contarPermisos = (estado: any) =>
  (estado.filaExistente ? 0 : 1)
  + (estado.acciones || []).filter((a: any) => !a.permisoExistente).length
  + (estado.secciones || []).length;

/** Cuántas cosas marcó la persona (sin contar la pantalla, que siempre está). */
export const contarMarcas = (estado: any) => (estado.acciones || []).length + (estado.secciones || []).length;

/** Indicador de pasos: 1 Elegir pantalla · 2 Marcar · 3 Revisar y guardar. Sin pantalla va en el 1; con pantalla y sin marcas, en el 2; con marcas, en el 3; ya guardado, los tres quedan hechos. */
export const pasosDeAgregar = (hayPantalla: any, marcas: any, guardado: any) => {
  const actual = guardado ? 4 : (!hayPantalla ? 1 : (marcas > 0 ? 3 : 2));
  return ['Elegir pantalla', 'Marcar', 'Revisar y guardar'].map((titulo: any, i: any) => ({
    numero: i + 1, titulo, hecho: i + 1 < actual, actual: i + 1 === actual
  }));
};

/** «Sin marcas» / «1 marca» / «3 marcas». */
export const textoContadorMarcas = (n: any) => (!n ? 'Sin marcas' : (n === 1 ? '1 marca' : n + ' marcas'));

/** Cada permiso que se va a crear, con el código que le tocará: sirve para validar y para explicar un 409. */
export const codigosDeEstado = (estado: any) => {
  const lista: any[] = [];
  const fe = estado.filaExistente;
  const base = fe ? fe.module + '.' + fe.submodule : estado.pantalla + '.' + slug(estado.nombre);
  if (!fe) lista.push({ codigo: base + '.read', fila: recortar(estado.nombre), casilla: 'Consultar' });
  for (const a of estado.acciones || []) {
    if (a.permisoExistente) continue;
    lista.push({ codigo: base + '.' + codigoAccion(a.casilla), fila: recortar(estado.nombre), casilla: a.casilla });
  }
  for (const s of estado.secciones || []) {
    lista.push({ codigo: estado.pantalla + '.' + slug(s.nombre) + '.read', fila: recortar(s.nombre), casilla: 'Consultar' });
  }
  return lista;
};

/** Lo que falta o choca antes de guardar (frases claras). Vacío = se puede guardar. */
export const problemasDeEstado = (estado: any, filasExistentes: any) => {
  const p: string[] = [];
  if (!estado) return p;
  if (!estado.pantalla) p.push('Elige el módulo donde va esta pantalla.');
  if (estado.filaExistente) { /* la fila ya existe: su nombre no se toca */ }
  else if (!recortar(estado.nombre)) p.push('Escribe el nombre que tendrá esta pantalla en la matriz.');
  else if (!slug(estado.nombre)) p.push('El nombre de la pantalla necesita al menos una letra o un número.');
  const vistos: any = {};
  for (const c of codigosDeEstado(estado)) {
    if (vistos[c.codigo]) p.push('Dos permisos nuevos quedarían con el mismo código: «' + c.casilla + ' ' + c.fila + '».');
    vistos[c.codigo] = true;
  }
  const yaHay = (filasExistentes || []).map((f: any) => slug(f.nombre));
  const nuevas = (estado.filaExistente ? [] : [estado.nombre]).concat((estado.secciones || []).map((s: any) => s.nombre));
  for (const n of nuevas) {
    if (slug(n) && yaHay.indexOf(slug(n)) >= 0) p.push('Ya existe «' + recortar(n) + '» en la matriz de este módulo: cámbiale el nombre.');
  }
  return p.filter((x, i) => p.indexOf(x) === i);
};

/**
 * El cuerpo EXACTO de POST /api/admin/rbac/agregar-permisos. `asignarARoles` (opcional) son los códigos de los roles a
 * los que se les dan los permisos nuevos de una vez; sin roles (o `null`) el cuerpo no lleva esa llave.
 */
export const armarCuerpo = (estado: any, asignarARoles: any = null) => {
  const fila = recortar(estado.nombre);
  const accionesCuerpo = (estado.acciones || []).map((a: any) => {
      const x: any = { casilla: a.casilla, huella: a.huella };
      if (a.permisoExistente) x.permisoExistente = a.permisoExistente;
      if (a.servidor && a.servidor.metodo && a.servidor.ruta) x.servidor = { metodo: a.servidor.metodo, ruta: a.servidor.ruta };
      return x;
    });
  // Pantalla que ya es una fila: se agrega a ESA fila (sin Consultar nuevo ni marca de submódulo).
  const fe = estado.filaExistente;
  const submodulos: any[] = [fe
    ? { filaExistente: { module: fe.module, submodule: fe.submodule }, acciones: accionesCuerpo }
    : { fila, despuesDe: estado.despuesDe || null, marca: { tipo: 'pantalla', huella: null }, acciones: accionesCuerpo }];
  // Las secciones van en cadena: la primera justo debajo de la pantalla, la siguiente debajo de la anterior.
  let anterior = fila;
  for (const s of estado.secciones || []) {
    const nombre = recortar(s.nombre);
    submodulos.push({ fila: nombre, despuesDe: anterior, marca: { tipo: 'seccion', huella: s.huella }, acciones: [] });
    anterior = nombre;
  }
  const cuerpo: any = { ruta: estado.rutaPatron, pantalla: estado.pantalla, grupo: estado.grupo || '', submodulos };
  if (Array.isArray(asignarARoles) && asignarARoles.length) cuerpo.asignarARoles = asignarARoles;
  return cuerpo;
};

// ── Lo que se dibuja sobre la pantalla (rbac-marcador:marcas) ──

/** code → «Módulo › Fila › Casilla» de toda la matriz (para el tooltip «Ya tiene permiso» del iframe). */
export const nombresDePermisos = (pantallas: any) => {
  const m: any = {};
  for (const c of casillasDeMatriz(pantallas)) if (c && c.code) m[c.code] = c.texto;
  return m;
};

/** Marcas para el iframe: las de esta sesión («Acción · Crear») y las ya guardadas («Ya marcado»). */
export const marcasParaIframe = (estado: any, guardadas: any) => {
  const lista: any[] = [];
  for (const g of guardadas || []) {
    if (g && g.huella && g.huella.selector) lista.push({ huella: g.huella, etiqueta: 'Ya marcado', permiso: g.permiso || '', ya: true });
  }
  if (estado) {
    for (const a of estado.acciones || []) lista.push({ huella: a.huella, etiqueta: 'Acción · ' + a.casilla });
    for (const s of estado.secciones || []) lista.push({ huella: s.huella, etiqueta: 'Sección · ' + s.nombre });
  }
  return lista;
};

// ── ¿La pantalla abrió la ruta que se pidió? (`rbac-marcador:listo` trae la `ruta` en la que quedó) ──

const tramosAbiertos = (r: any) => String(r == null ? '' : r)
  .replace(/^[A-Za-z][A-Za-z0-9+.-]*:\/\/[^/]*/, '')
  .split(/[?#]/)[0]
  .split('/')
  .filter((s: string) => s !== '')
  .map((s: string) => {
    try { return decodeURIComponent(s); } catch (e) { return s; }
  });

/**
 * ¿La ruta en que quedó la pantalla es la que se pidió (con los valores de ejemplo puestos)? Se compara tramo por
 * tramo ya decodificado, sin query, hash ni barra final. Sin ruta (o con otra) es que la pantalla redirigió.
 */
export const rutaAbiertaCoincide = (pedida: any, recibida: any) => {
  if (typeof recibida !== 'string' || recibida === '') return false;
  const a = tramosAbiertos(pedida);
  const b = tramosAbiertos(recibida);
  return a.length === b.length && a.every((x: any, i: any) => x === b[i]);
};

/** Lo que se le dice al administrador cuando la pantalla lo mandó a otra ruta (guard, valor de ejemplo inválido). */
export const textoRutaDesviada = (ruta: any) =>
  'La pantalla te mandó a otra ruta (' + (typeof ruta === 'string' && ruta ? ruta : 'desconocida')
  + '). Revisa el valor de ejemplo o tus permisos.';

/** Marcas ya guardadas que pertenecen a esta ruta (la ruta guardada es el patrón con `:param`). */
export const marcasDeRuta = (marcas: any, rutaPatron: any) =>
  (marcas || []).filter((m: any) => m && m.ruta === rutaPatron);

// ── Dar los permisos nuevos a los roles que hoy ya ven la pantalla (GET /roles-que-ven, `asignarARoles`) ──

/** Ayuda bajo la casilla (en ámbar cuando está desmarcada): qué pasa si no se les dan los permisos. */
export const TEXTO_SIN_ROLES =
  'Si no se los das, quienes hoy usan esta pantalla dejarán de verla (o de ver los botones marcados) hasta que su rol reciba el permiso en la matriz.';

const porNombre = (a: any, b: any) => String(a.name || a.code).localeCompare(String(b.name || b.code), 'es');

/**
 * Parte los roles que devuelve el API en tres: los que van en la lista con casilla (`ven` y los que se agregaron a mano
 * en `extras`), los que ya tienen todo (se nombran aparte) y los demás, para «Agregar otro rol».
 */
export const repartirRoles = (roles: any, extras: any) => {
  const validos = (roles || []).filter((r: any) => r && typeof r.code === 'string' && r.code);
  const yaTienenTodo = validos.filter((r: any) => r.yaTieneTodo === true).sort(porNombre);
  const resto = validos.filter((r: any) => r.yaTieneTodo !== true);
  const agregado = (r: any) => (extras || []).indexOf(r.code) >= 0;
  const lista = resto.filter((r: any) => r.ven === true).sort(porNombre)
    .concat((extras || []).map((c: any) => resto.find((r: any) => r.code === c && r.ven !== true)).filter(Boolean));
  const otros = resto.filter((r: any) => r.ven !== true && !agregado(r)).sort(porNombre);
  return { lista, yaTienenTodo, otros };
};

/** Los roles que arrancan con la casilla marcada: los que hoy ven la pantalla (sin los que ya tienen todo). */
export const rolesPreseleccionados = (roles: any) =>
  (roles || []).filter((r: any) => r && r.code && r.ven === true && r.yaTieneTodo !== true).map((r: any) => r.code);

/** Marca o desmarca un rol (devuelve la lista nueva, sin repetir). */
export const alternarRol = (elegidos: any, codigo: any) =>
  (elegidos || []).indexOf(codigo) >= 0 ? (elegidos || []).filter((c: any) => c !== codigo) : (elegidos || []).concat([codigo]);

/**
 * Lo que va en `asignarARoles` del POST: los roles marcados que de verdad existen en la lista y no tienen ya todo.
 * Con la casilla general desmarcada, o sin ninguno marcado, es `null` (el cuerpo no lleva la llave).
 */
export const rolesAAsignar = (dar: any, elegidos: any, roles: any) => {
  if (!dar) return null;
  const validos = (roles || []).filter((r: any) => r && r.code && r.yaTieneTodo !== true).map((r: any) => r.code);
  const lista = (elegidos || []).filter((c: any, i: any) => validos.indexOf(c) >= 0 && elegidos.indexOf(c) === i);
  return lista.length ? lista : null;
};

/** «Ya tienen todo: Super Administrador, Gerente» (vacío si no hay ninguno). */
export const textoYaTienenTodo = (roles: any) => {
  const nombres = (roles || []).map((r: any) => recortar(r.name) || r.code);
  return nombres.length ? 'Ya tienen todo: ' + nombres.join(', ') : '';
};

/** «3 de 5 roles marcados» bajo la lista. */
export const resumenRolesMarcados = (elegidos: any, lista: any) => {
  const codigos = (lista || []).map((r: any) => r.code);
  const n = (elegidos || []).filter((c: any) => codigos.indexOf(c) >= 0).length;
  const t = codigos.length;
  return n + ' de ' + t + (t === 1 ? ' rol marcado' : ' roles marcados');
};

/** Cuántos roles recibieron los permisos según la respuesta del POST (`data.asignados`); `null` si la respuesta no lo trae. */
export const cuantosAsignados = (resultado: any) =>
  resultado && Array.isArray(resultado.asignados) ? resultado.asignados.length : null;

/** Frase de éxito: «Los permisos se dieron a 3 roles.» Vacía si no se pudo saber (respuesta sin `asignados`). */
export const mensajeAsignados = (resultado: any) => {
  const n = cuantosAsignados(resultado);
  if (n === null) return '';
  if (n === 0) return 'No se dieron a ningún rol. Quienes hoy usan esta pantalla no la verán hasta que su rol reciba el permiso en la matriz.';
  return 'Los permisos se dieron a ' + (n === 1 ? '1 rol' : n + ' roles') + '.';
};

// ── Rutas del servidor ──

const ETIQUETA_METODO: any = { GET: 'Consultar', POST: 'Crear o enviar', PUT: 'Cambiar', PATCH: 'Cambiar', DELETE: 'Borrar' };

/** Palabra clara para el método: GET → Consultar. */
export const etiquetaMetodo = (metodo: any) => {
  const m = String(metodo || '').toUpperCase();
  return Object.prototype.hasOwnProperty.call(ETIQUETA_METODO, m) ? ETIQUETA_METODO[m] : m;
};

/** Buscador de «Qué cuida también en el servidor»: todas las palabras deben aparecer; primero el método esperado. */
export const filtrarRutasServidor = (rutas: any, texto: any, metodo: any, max: any = 8) => {
  const palabras = sinTildes(recortar(texto)).split(' ').filter(Boolean);
  const coincide = (r: any) => {
    const t = sinTildes((r.metodo || '') + ' ' + (r.ruta || ''));
    return palabras.every((w) => t.indexOf(w) >= 0);
  };
  const todas = (rutas || []).filter((r: any) => r && r.ruta && coincide(r));
  const ordenadas = todas
    .map((r: any, i: any) => ({ r, i, p: metodo && String(r.metodo).toUpperCase() === metodo ? 0 : 1 }))
    .sort((a: any, b: any) => a.p - b.p || a.i - b.i)
    .map((x: any) => x.r);
  return { lista: ordenadas.slice(0, max), total: ordenadas.length };
};

// ── 409: códigos repetidos ──

/** Saca la lista de códigos repetidos de la respuesta 409 (acepta varias formas: data, repetidos, codigos...). */
export const codigosRepetidos = (error: any) => {
  const e = error && error.error ? error.error : error;
  const d = e && e.data;
  const candidatos = [d, e && e.repetidos, e && e.codigos, e && e.existentes,
    d && d.repetidos, d && d.codigos, d && d.existentes, d && d.duplicados];
  for (const c of candidatos) {
    if (Array.isArray(c)) {
      return c.map((x: any) => (typeof x === 'string' ? x : (x && (x.code || x.codigo)) || '')).filter(Boolean);
    }
  }
  return [];
};

/** Mensaje claro para el 409: qué permiso choca y qué hacer. No se guardó nada. */
export const mensajeRepetidos = (estado: any, codigos: any) => {
  const conocidos = estado ? codigosDeEstado(estado) : [];
  const nombres = (codigos || []).map((c: any) => {
    const k = conocidos.find((x: any) => x.codigo === c);
    return k ? '«' + k.casilla + ' ' + k.fila + '»' : c;
  });
  if (!nombres.length) return 'No se guardó nada: alguno de los permisos ya existe en la matriz. Cambia el nombre de la pantalla, de la sección o de la acción y vuelve a guardar.';
  return 'No se guardó nada: ya existe en la matriz ' + nombres.join(', ') + '. Cambia el nombre de la pantalla, de la sección o de la acción que choca y vuelve a guardar.';
};

// ── «Lo que también cuida el servidor»: encender y apagar las protecciones (GET/PUT /protecciones) ──

/** Con más protecciones que estas se ofrece el filtro de texto. */
export const MAX_SIN_FILTRO = 8;

/** ¿Vale la pena mostrar el buscador? Solo cuando la lista ya es larga. */
export const hayFiltroProtecciones = (lista: any) => (lista || []).length > MAX_SIN_FILTRO;

/**
 * Nombre que la matriz le da a un permiso («Crear Tasas de mora»). Sale del código `<módulo>.<fila>.<acción>` y de las
 * filas que ya tenemos a mano (GET /pantallas); si no se puede deducir con certeza se devuelve el código tal cual.
 */
export const nombreDePermiso = (codigo: any, pantallas: any) => {
  const c = recortar(codigo);
  const partes = c.split('.');
  if (partes.length !== 3) return c;
  const pantalla = (pantallas || []).find((p: any) => p && p.id === partes[0]);
  const fila = pantalla && (pantalla.filas || []).find((f: any) => f && slug(f.nombre) === partes[1]);
  const casilla = Object.keys(ACCION_CODIGO).find((k) => ACCION_CODIGO[k] === partes[2]);
  return fila && casilla ? casilla + ' ' + recortar(fila.nombre) : c;
};

/** «Parámetros del Sistema › Seguridad Avanzada › Prueba historial servidor»; si la casilla no está en el árbol, el código. */
export const rutaLegibleDePermiso = (codigo: any, pantallas: any) => {
  const c = recortar(codigo);
  for (const p of pantallas || []) {
    for (const f of (p && p.filas) || []) {
      const casilla = ((f && f.casillas) || []).find((x: any) => x && x.code === c);
      if (casilla) return [p.nombre, f.nombre, casilla.nombre].map(recortar).filter(Boolean).join(' › ');
    }
  }
  return c;
};

/** Estado en palabras: lo que se ve en la etiqueta de la fila. */
export const estadoProteccion = (activo: any) => (activo === true ? 'Encendido' : 'Apagado');

/**
 * Las filas que se dibujan: una por protección, con el nombre visible del permiso y el estado en palabras.
 * `texto` filtra (todas las palabras deben aparecer en la llamada, el permiso o el estado); vacío = todas.
 */
export const filasProtecciones = (lista: any, pantallas: any, texto: any) => {
  const palabras = sinTildes(recortar(texto)).split(' ').filter(Boolean);
  return (lista || [])
    .filter((p: any) => p && p._id)
    .map((p: any) => ({
      id: String(p._id),
      metodo: String(p.metodo || '').toUpperCase(),
      ruta: String(p.ruta || ''),
      permiso: String(p.permiso || ''),
      nombre: nombreDePermiso(p.permiso, pantallas),
      donde: rutaLegibleDePermiso(p.permiso, pantallas),
      activo: p.activo === true,
      estado: estadoProteccion(p.activo)
    }))
    .filter((f: any) => {
      const t = sinTildes([f.metodo, f.ruta, f.nombre, f.donde, f.permiso, f.estado].join(' '));
      return palabras.every((w) => t.indexOf(w) >= 0);
    });
};

/** «3 llamadas · 1 encendida»: el resumen del encabezado. */
export const resumenProtecciones = (lista: any) => {
  const todas = (lista || []).filter((p: any) => p && p._id);
  if (!todas.length) return 'Todavía no hay ninguna llamada registrada.';
  const n = todas.filter((p: any) => p.activo === true).length;
  const llamadas = todas.length === 1 ? '1 llamada' : todas.length + ' llamadas';
  const encendidas = n === 1 ? '1 encendida' : n + ' encendidas';
  return llamadas + ' · ' + encendidas;
};

/** La advertencia que se muestra dentro de la página antes de ENCENDER (apagar no pide nada). */
export const textoEncender = (nombre: any) =>
  'Desde ahora el servidor exige el permiso «' + recortar(nombre) + '» para esta llamada. ' +
  'Si la usa otra aplicación o un usuario sin ese permiso, quedará frenada.';

/** Error al encender o apagar, en lenguaje claro y diciendo qué hacer. */
export const mensajeErrorProteccion = (error: any, encender: any) => {
  const que = encender ? 'encender' : 'apagar';
  const estado = error && error.status;
  if (estado === 0) return 'No se pudo ' + que + ' la protección: no hay conexión con el servidor. Revisa tu internet e intenta de nuevo.';
  if (estado === 404) return 'Esa protección ya no existe (alguien pudo haberla quitado). Ya actualicé la lista.';
  if (estado === 400) return 'No se pudo ' + que + ' la protección: la solicitud no era válida. Actualiza la lista e intenta de nuevo.';
  if (estado === 401) return 'Tu sesión venció. Vuelve a entrar y repite el cambio.';
  if (estado === 403) return 'No tienes permiso para ' + que + ' protecciones del servidor.';
  const msg = error && error.error && typeof error.error.msg === 'string' ? recortar(error.error.msg) : '';
  return msg ? 'No se pudo ' + que + ' la protección: ' + msg : 'No se pudo ' + que + ' la protección. Intenta de nuevo en un momento.';
};

// ── Fase 5 · Pantalla que ya está en la matriz, permisos que ya existen y módulos nuevos ──

/** Valor de la opción «+ Nuevo módulo» del selector de módulo. */
export const MODULO_NUEVO = '__nuevo__';

/** Casilla de la fila cuyo nombre coincide con el texto (sin tildes ni mayúsculas); `null` si no hay fila o casilla. */
export const casillaDeFila = (fila: any, texto: any) => {
  const q = sinTildes(recortar(texto));
  if (!fila || !q) return null;
  return (fila.casillas || []).find((c: any) => c && sinTildes(recortar(c.nombre)) === q) || null;
};

/** Todas las casillas de la matriz: { code, nombre, fila, modulo, texto: 'Módulo › Fila › Casilla' }. */
export const casillasDeMatriz = (pantallas: any) => {
  const lista: any[] = [];
  for (const p of pantallas || []) {
    for (const f of (p && p.filas) || []) {
      for (const c of (f && f.casillas) || []) {
        if (!c || !c.code) continue;
        lista.push({ code: c.code, nombre: c.nombre, fila: f.nombre, modulo: p.nombre, texto: p.nombre + ' › ' + f.nombre + ' › ' + c.nombre });
      }
    }
  }
  return lista;
};

/** Buscador de «Uno que ya existe»: por módulo, fila, casilla o código; sin texto muestra las primeras `max`. */
export const buscarCasillas = (casillas: any, texto: any, max: any = 8) => {
  const q = sinTildes(recortar(texto));
  const todas = casillas || [];
  const palabras = q ? q.split(' ') : [];
  const lista = palabras.length
    ? todas.filter((c: any) => palabras.every((w: any) => sinTildes(c.texto + ' ' + c.code).indexOf(w) >= 0))
    : todas;
  return { lista: lista.slice(0, max), total: lista.length };
};

/**
 * La fila de la matriz que YA es esta pantalla. Primero por los permisos que exige la ruta (`data.permissions`) contra
 * los códigos de las casillas; si no, por una marca de tipo pantalla de esa ruta (su `permiso`). Con varias filas
 * distintas gana la de su Consultar (`.read`) y, si no, la primera; `otras` nombra las demás.
 * Devuelve { pantalla (id), modulo, nombre, module, submodule, grupo, casillas, origen: 'permisos'|'marca', otras } o `null`.
 */
export const buscarFilaDePantalla = (pantallas: any, permisos: any, marcas: any, rutaPatron: any) => {
  const filas: any[] = [];
  for (const p of pantallas || []) {
    for (const f of (p && p.filas) || []) filas.push({ p, f });
  }
  const dueña = (code: any) => filas.find((x: any) => (x.f.casillas || []).some((c: any) => c && c.code === code)) || null;
  const elegir = (codigos: any, origen: any) => {
    const halladas: any[] = [];
    for (const code of codigos) {
      const x = dueña(code);
      if (x && !halladas.some((h: any) => h.x.f === x.f)) halladas.push({ x, code });
    }
    if (!halladas.length) return null;
    const lectura = halladas.find((h: any) => /\.read$/.test(h.code));
    const h = lectura || halladas[0];
    const f = h.x.f;
    return {
      pantalla: h.x.p.id,
      modulo: h.x.p.nombre,
      nombre: f.nombre,
      module: f.module,
      submodule: f.submodule,
      grupo: f.grupo || '',
      casillas: (f.casillas || []).map((c: any) => ({ code: c.code, nombre: c.nombre })),
      origen,
      otras: halladas.filter((o: any) => o !== h).map((o: any) => o.x.f.nombre)
    };
  };
  const dePermisos = elegir((permisos || []).filter((c: any) => typeof c === 'string'), 'permisos');
  if (dePermisos) return dePermisos;
  const deMarca = (marcas || [])
    .filter((m: any) => m && m.tipo === 'pantalla' && m.ruta === rutaPatron && typeof m.permiso === 'string')
    .map((m: any) => m.permiso);
  return elegir(deMarca, 'marca');
};

/**
 * Rutas que no declaran `data.permissions` (cuidadas por IsProfileGuard): su entrada en URL_MODULE_MAP dice el
 * módulo y submódulo (`/console/invoice-dashboard` → ventas.facturas). Devuelve los códigos de las casillas de la
 * matriz con ese prefijo, para que buscarFilaDePantalla encuentre la fila (Compras Financiadas) en vez de crear otra.
 */
export const codigosDelMapaDeRuta = (rutaPatron: any, mapa: any, pantallas: any) => {
  const m = (mapa || []).find((x: any) => x && x.pattern && String(rutaPatron).includes(x.pattern));
  if (!m || !m.submodule) return [];
  const prefijos = (m.modules || []).map((mod: any) => mod + '.' + m.submodule + '.');
  const codigos: string[] = [];
  for (const p of pantallas || []) {
    for (const f of (p && p.filas) || []) {
      for (const c of (f && f.casillas) || []) {
        if (c && typeof c.code === 'string' && prefijos.some((pre: any) => c.code.indexOf(pre) === 0)) codigos.push(c.code);
      }
    }
  }
  return codigos;
};

/**
 * Cómo se llama en la matriz la pantalla de una ruta, para la lista del buscador: «Perfil del Contacto · Contactos»
 * (fila · módulo). Primero por los permisos de la ruta (`data.permissions`), si no por URL_MODULE_MAP. '' si no se sabe.
 */
export const nombreMatrizDeRuta = (rutaPatron: any, permisosRuta: any, mapa: any, pantallas: any) => {
  const permisos = (permisosRuta || []).length ? permisosRuta : codigosDelMapaDeRuta(rutaPatron, mapa, pantallas);
  const fila = permisos.length ? buscarFilaDePantalla(pantallas, permisos, [], rutaPatron) : null;
  return fila && fila.nombre ? recortar(fila.nombre) + ' · ' + recortar(fila.modulo) : '';
};

/** Pregunta y ayuda del campo de ejemplo de un parámetro de la ruta (`:code`), en palabras de todos los días. */
export const ayudaDeParametro = (param: any) => {
  const p = recortar(param);
  const k = p.toLowerCase().replace(/[^a-z0-9]/g, '');
  if (k === 'code' || k === 'codeperson' || k === 'codeorid') return 'Código del cliente (lo ves en la ficha del cliente)';
  if (k === 'merchant' || k === 'merchantcode' || k === 'codigocomercio') return 'Código del comercio';
  if (k === 'id') return 'Identificador del registro';
  return 'Dato «' + p + '» de la dirección';
};

/** Estado con la pantalla puesta como esa fila existente: módulo y grupo salen de ella, el nombre no se edita. */
export const conFilaExistente = (estado: any, fila: any) => ({
  ...estado,
  pantalla: fila.pantalla,
  grupo: fila.grupo || '',
  nombre: fila.nombre,
  despuesDe: '',
  filaExistente: { module: fila.module, submodule: fila.submodule, nombre: fila.nombre, casillas: fila.casillas }
});

/** «Crear un submódulo nuevo en su lugar»: vuelve al comportamiento de siempre (submódulo nuevo con su Consultar). */
export const sinFilaExistente = (estado: any, pantallas: any) => ({
  ...estado,
  filaExistente: null,
  nombre: nombreDeRuta(estado.rutaPatron),
  pantalla: moduloDeRuta(estado.rutaPatron, pantallas),
  grupo: '',
  despuesDe: ''
});

/** Frase de la tarjeta fija cuando la pantalla ya es una fila. */
export const textoFilaExistente = (fila: any) =>
  'Esta pantalla ya es la fila «' + (fila && fila.nombre) + '» de «' + (fila && fila.modulo) + '»';

/** Aviso cuando la ruta exige permisos de varias filas: cuál se eligió y cuáles quedaron fuera. */
export const textoFilasElegida = (fila: any) =>
  fila && fila.otras && fila.otras.length
    ? 'La ruta exige permisos de varias filas; se eligió «' + fila.nombre + '». También usa: ' + fila.otras.map((n: any) => '«' + n + '»').join(', ') + '.'
    : '';

// ── Módulo nuevo (POST /api/admin/rbac/pantallas) ──

/** Por qué no se puede crear el módulo (texto claro), o '' si está bien. */
export const problemaDeModulo = (nombre: any, pantallas: any) => {
  const n = recortar(nombre);
  if (!n) return 'Escribe el nombre del módulo.';
  if (!slug(n)) return 'El nombre del módulo necesita al menos una letra o un número.';
  const igual = (pantallas || []).find((p: any) => slug(p.nombre) === slug(n));
  return igual ? 'Ya existe un módulo llamado «' + igual.nombre + '»: elige otro nombre.' : '';
};

/** Cuerpo de POST /pantallas: `despuesDe` es el id del módulo anterior o `null` = al final. */
export const cuerpoModulo = (nombre: any, despuesDe: any) => ({ nombre: recortar(nombre), despuesDe: despuesDe || null });

/** El error del POST /pantallas en lenguaje claro (409 = nombre repetido). */
export const mensajeErrorModulo = (error: any) => {
  if (error && error.status === 409) return 'Ya existe un módulo con ese nombre. Elige otro.';
  const msg = error && error.error && error.error.msg;
  return typeof msg === 'string' && msg ? msg : 'No se pudo crear el módulo. Revisa la conexión e intenta de nuevo.';
};

// ── Rediseño del panel derecho (26-oct): lenguaje llano, sin tecnicismos fuera del plegable del equipo técnico ──

/** El tipo no se pregunta: botón, enlace o campo = acción; contenedor = sección. */
export const deducirTipo = (sugerencia: any) => (sugerencia && sugerencia.tipo === 'seccion' ? 'seccion' : 'accion');

/** Cómo se nombra lo tocado en la pregunta: su texto, o «este botón» / «esta parte de la pantalla» si no tiene. */
export const nombreDelElemento = (form: any) => {
  const t = recortar(form && form.huella && form.huella.texto);
  if (t) return '«' + t + '»';
  return form && form.tipo === 'seccion' ? 'esta parte de la pantalla' : 'este botón';
};

/** El título de «Lo que tocaste»: «¿Quién puede usar «Eliminar»?» (acción) o «¿Quién puede ver «Historial»?» (sección). */
export const preguntaQuien = (form: any) =>
  '¿Quién puede ' + (form && form.tipo === 'seccion' ? 'ver' : 'usar') + ' ' + nombreDelElemento(form) + '?';

/** Nota bajo la pregunta: vacía si coincide con un solo elemento (lo normal). */
export const notaCoincidencias = (form: any) => {
  const n = Number(form && form.coincidencias) || 0;
  if (form && form.alcance === 'solo' && form.huellaTodos) return 'Solo este: los demás parecidos quedan sin marcar.';
  const que = form && form.tipo === 'seccion' ? 'Esta parte' : 'Este botón';
  if (n === 0) return 'No lo volví a encontrar en la pantalla: si no se ve, la marca no va a esconder nada.';
  if (n === 1) return '';
  return que + ' aparece ' + n + ' veces en la pantalla (por ejemplo, una por fila). Se aplicará a todos.';
};

/** «Módulo › Fila › Casilla» → «Casilla» (el último tramo). */
export const ultimoTramo = (texto: any) => {
  const partes = String(texto == null ? '' : texto).split('›');
  return recortar(partes[partes.length - 1]);
};

/** «Así se verá en la matriz: Tasas de mora › Exportar». */
export const comoSeVera = (fila: any, nombre: any) =>
  'Así se verá en la matriz: ' + (recortar(fila) || 'esta pantalla') + ' › ' + (recortar(nombre) || '…');

/** Lo que se muestra como «Lo que tocaste»: «Botón «Eliminar»», «Sección «Historial»», o algo genérico si no hay etiqueta. */
export const queTocaste = (form: any) => {
  const e = recortar(form && form.huella && form.huella.etiqueta);
  if (e) return e;
  return form && form.tipo === 'seccion' ? 'Esta parte de la pantalla' : 'Este botón';
};

/** Aviso corto bajo el nombre: dónde queda la casilla nueva y quién la tendrá. */
export const dondeSeAgrega = (modulo: any, pantalla: any) =>
  'Se agrega a la Matriz de Permisos en ' + (recortar(modulo) || 'este módulo') + ' › ' + (recortar(pantalla) || 'esta pantalla')
  + '. Lo tendrán los roles que hoy ven esta pantalla; para quitárselo a un rol, desmárcalo en la matriz.';

/** Frase de cada marca de la lista «Lo que marcaste». */
export const detalleDeMarca = (marca: any, esSeccion: any) => {
  if (esSeccion) return 'Sección aparte en la matriz';
  return marca && marca.permisoExistente ? 'Los mismos que ya pueden ' + ultimoTramo(marca.permisoTexto || marca.casilla) : 'Opción nueva';
};

/** «Vas a agregar 1 opción nueva y 1 marca a Seguridad Avanzada». */
export const resumenAgregar = (estado: any, nombreModulo: any) => {
  if (!estado) return '';
  const o = contarPermisos(estado);
  const m = contarMarcas(estado);
  const modulo = recortar(nombreModulo);
  const partes: string[] = [];
  if (o > 0) partes.push(o + (o === 1 ? ' opción nueva' : ' opciones nuevas'));
  if (m > 0) partes.push(m + (m === 1 ? ' marca' : ' marcas'));
  if (!partes.length) return 'Todavía no marcaste nada.';
  return 'Vas a agregar ' + partes.join(' y ') + (modulo ? ' a ' + modulo : '') + '.';
};

/** «12 roles» / «1 rol» / «Nadie»: lo que se ve en la línea cerrada de «¿Quién lo verá?». */
export const resumenQuienVera = (dar: any, elegidos: any, lista: any) => {
  if (!dar) return 'Nadie por ahora';
  const codigos = (lista || []).map((r: any) => r.code);
  const n = (elegidos || []).filter((c: any) => codigos.indexOf(c) >= 0).length;
  return n === 0 ? 'Nadie por ahora' : (n === 1 ? '1 rol' : n + ' roles');
};

/** Palabra llana para el método: GET → «Consultar datos». */
export const textoLlanoMetodo = (metodo: any) => {
  const m = String(metodo || '').toUpperCase();
  const t: any = { GET: 'Consultar datos', POST: 'Crear', PUT: 'Guardar cambios', PATCH: 'Guardar cambios', DELETE: 'Borrar' };
  return Object.prototype.hasOwnProperty.call(t, m) ? t[m] : m;
};

const tramosDeUrl = (u: any) => String(u == null ? '' : u)
  .replace(/^[A-Za-z][A-Za-z0-9+.-]*:\/\/[^/]*/, '')
  .split(/[?#]/)[0]
  .split('/')
  .filter((s: string) => s !== '');

/** La dirección sin dominio ni query: `https://x.com/api/planes/5?a=1` → `/api/planes/5`. */
export const urlSinDominio = (u: any) => '/' + tramosDeUrl(u).join('/');

const casaConPatron = (patron: any, url: any) => {
  const p = tramosDeUrl(patron);
  const u = tramosDeUrl(url);
  return p.length === u.length && p.every((s: string, i: number) => s.charAt(0) === ':' || s === u[i]);
};

const sinParametros = (patron: any) => tramosDeUrl(patron).filter((s: string) => s.charAt(0) === ':').length;

/**
 * Las llamadas del servidor que la propia pantalla hizo al cargarse (`[{ metodo, url }]`, dichas por el marcador) cruzadas con
 * las del servidor (`[{ metodo, ruta }]`, con `:param`). Si una dirección calza con varias, gana la más exacta (menos `:param`).
 * Sin repetir, en el orden en que la pantalla las pidió.
 */
export const llamadasUsadas = (rutasServidor: any, llamadas: any) => {
  const salida: any[] = [];
  for (const l of llamadas || []) {
    if (!l || !l.url) continue;
    const metodo = String(l.metodo || '').toUpperCase();
    const calzan = (rutasServidor || []).filter((r: any) => r && r.ruta && String(r.metodo).toUpperCase() === metodo && casaConPatron(r.ruta, l.url));
    if (!calzan.length) continue;
    const menos = Math.min(...calzan.map((r: any) => sinParametros(r.ruta)));
    for (const r of calzan.filter((x: any) => sinParametros(x.ruta) === menos)) {
      if (!salida.some((x: any) => x === r)) salida.push(r);
    }
  }
  return salida;
};

const PALABRAS_VACIAS = ['de', 'del', 'la', 'las', 'el', 'los', 'por', 'con', 'para', 'console'];

/** Palabras del nombre de la pantalla para parecerse a una llamada: «tasas-de-mora» → ['tasa', 'mora'] (sin plural). */
export const palabrasDeRuta = (rutaPatron: any) =>
  tramosDeUrl(rutaPatron)
    .filter((s: string) => s.charAt(0) !== ':')
    .reduce((a: any, t: string) => a.concat(sinTildes(t).split(/[^a-z0-9]+/)), [])
    .filter((w: string) => w.length >= 3 && PALABRAS_VACIAS.indexOf(w) < 0)
    .map((w: string) => (w.length > 4 && w.charAt(w.length - 1) === 's' ? w.slice(0, -1) : w));

/**
 * Lo que se ofrece en «Opciones del equipo técnico», en este orden: las que la pantalla usó al cargarse (`usadas`) y las que se
 * parecen al nombre de la pantalla (`parecidas`, sin repetir las anteriores, con el método esperado primero). El buscador completo
 * (`filtrarRutasServidor`) queda detrás de «Buscar en todas».
 */
export const sugerenciasServidor = (rutasServidor: any, llamadas: any, rutaPatron: any, metodo: any, max: any = 6) => {
  const primero = (a: any, b: any) => (metodo && String(b.r.metodo).toUpperCase() === metodo ? 1 : 0) - (metodo && String(a.r.metodo).toUpperCase() === metodo ? 1 : 0);
  const usadas = llamadasUsadas(rutasServidor, llamadas)
    .map((r: any, i: any) => ({ r, i }))
    .sort((a: any, b: any) => primero(a, b) || a.i - b.i)
    .map((x: any) => x.r);
  const palabras = palabrasDeRuta(rutaPatron);
  const parecidas = (rutasServidor || [])
    .filter((r: any) => r && r.ruta && usadas.indexOf(r) < 0)
    .map((r: any, i: any) => {
      const t = sinTildes(r.ruta);
      return { r, i, n: palabras.filter((w: string) => t.indexOf(w) >= 0).length };
    })
    .filter((x: any) => x.n > 0)
    .sort((a: any, b: any) => b.n - a.n || primero(a, b) || a.i - b.i)
    .slice(0, max)
    .map((x: any) => x.r);
  return { usadas: usadas.slice(0, max), parecidas };
};

/** Lo que manda el marcador (`rbac-marcador:llamadas`) limpio: solo { metodo, url } de textos, sin repetir y con tope. */
export const llamadasDelMensaje = (msg: any, max: any = 200) => {
  const lista: any[] = [];
  for (const l of (msg && Array.isArray(msg.llamadas) ? msg.llamadas : [])) {
    if (!l || typeof l.metodo !== 'string' || typeof l.url !== 'string' || !l.url) continue;
    const metodo = l.metodo.toUpperCase();
    const url = urlSinDominio(l.url);
    if (!lista.some((x: any) => x.metodo === metodo && x.url === url)) lista.push({ metodo, url });
    if (lista.length >= max) break;
  }
  return lista;
};
